import React, { useEffect, useMemo, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { Header } from 'components/Header';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useMutation } from 'services/api/hooks';
import { exportAllAnalysisData } from 'services/candidate/api';
import { Paths } from 'services/navigation/paths';
import { isProductLimitReachedSelector } from 'services/billing/atoms';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { labelByValue, TabsValue } from './constants';
import { SearchInput } from 'components/Inputs/SearchInput';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { useCandidateReportDrawerParams } from './hooks/useCandidateReportDrawerParams';
import { LiveStatusIndicatorV2 } from 'common/ui';

import { useIsAdmin } from 'hooks/useIsAdmin';
import { AnalysesTab } from 'modules/analyse/components/AnalysesTab';
import { SubscriptionProduct } from 'generated-graphql/types';
import { ApplicantsTab } from 'modules/analyse/components/ApplicantsTab';
import { useAnalysesPageSearch } from './hooks/useAnalysesPageSearch';
import { TabsV2 } from 'common/ui/TabsV2';
import { SourceCandidatesTab } from 'modules/analyse/components/SourceCandidatesTab';
import { useTabTotals } from './hooks/useTabTotals';
import { ApolloError } from '@apollo/client';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';

const container: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const headerContainer: SxProps = {
  display: 'flex',
  mb: '40px',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const userActionsContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
};

export const CandidateRankings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  useCandidateReportDrawerParams();
  const navigate = useNavigate();

  const { isAdmin } = useIsAdmin();
  const isPoppScoringLimitReached = useRecoilValue(
    isProductLimitReachedSelector(SubscriptionProduct.PoppScoring)
  );

  const [isAllCampaigns, setIsAllCampaigns] = useState(false);
  const [applicantsSubscriptionError, setApplicantsSubscriptionError] =
    useState<ApolloError | undefined>();
  const [
    sourcedCandidatesSubscriptionError,
    setSourcedCandidatesSubscriptionError
  ] = useState<ApolloError | undefined>();

  const { analysesTotal, applicantsTotal, sourcedCandidatesTotal } =
    useTabTotals({
      isAllCampaigns
    });
  const [tab, setTab] = useState<TabsValue>(
    searchParams.get('tab')
      ? (searchParams.get('tab') as TabsValue)
      : TabsValue.ROLES
  );

  const { searchRole, setSearchRole } = useAnalysesPageSearch();

  const { track } = useTracking();

  const [limit, setLimit] = useState(50);

  const { mutate: exportDataAction, isLoading: isExporting } = useMutation(
    exportAllAnalysisData,
    {
      successTextMessage:
        'Data exported successfully. You will receive an email with the download link.',
      errorTextMessage: 'An error occurred while exporting data.'
    }
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: labelByValue[TabsValue.ROLES],
        value: TabsValue.ROLES,
        count: analysesTotal >= 10000 ? '10000+' : analysesTotal
      },
      {
        label: labelByValue[TabsValue.APPLICANTS],
        value: TabsValue.APPLICANTS,
        count: applicantsTotal >= 10000 ? '10000+' : applicantsTotal
      },
      {
        label: labelByValue[TabsValue.SOURCED_CANDIDATES],
        value: TabsValue.SOURCED_CANDIDATES,
        count:
          sourcedCandidatesTotal >= 10000 ? '10000+' : sourcedCandidatesTotal
      }
    ];

    return baseTabs;
  }, [analysesTotal, applicantsTotal, sourcedCandidatesTotal]);

  const handleRowsPerPageChange = async (newPerPage: number) => {
    setLimit(newPerPage);
    if (tab === TabsValue.ROLES) {
      track(TrackingEventName.AnalyseListRowsNumberUpdate, {
        rowsNumber: newPerPage
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      setIsAllCampaigns(true);
    }
  }, [isAdmin]);

  // Determine if subscriptions are live based on current tab
  const isLive = useMemo(() => {
    if (tab === TabsValue.APPLICANTS) {
      return !applicantsSubscriptionError;
    } else if (tab === TabsValue.SOURCED_CANDIDATES) {
      return !sourcedCandidatesSubscriptionError;
    }
    // ROLES tab doesn't have subscriptions, always show as live
    return true;
  }, [tab, applicantsSubscriptionError, sourcedCandidatesSubscriptionError]);

  return (
    <Box sx={container}>
      <Header
        title="Analyse"
        prevPageTitle="Analyse"
        subtitle={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Rank candidates by analysing their CV
            <LiveStatusIndicatorV2 isLive={isLive} />
          </Box>
        }
      />
      <TabsV2
        tabs={tabs}
        value={tab}
        onChange={(value) => {
          // Clear filters and update tab in a single operation
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('searchFields');
          newParams.delete('filterValues');
          newParams.set('tab', value);
          setSearchParams(newParams.toString(), {
            state: { shallow: true },
            replace: true
          });
          setTab(value as TabsValue);
        }}
        endItems={[
          <Button
            variant="tertiary"
            onClick={() => {
              exportDataAction();
              track(TrackingEventName.AnalyseListExport);
            }}
            disabled={isExporting}
          >
            Export all data
          </Button>,
          <Button
            variant="secondary"
            onClick={() => navigate(Paths.ANALYSE_TEMPLATES)}
          >
            Templates
          </Button>,
          <Button
            variant="secondary"
            onClick={() => navigate(Paths.ANALYSE_RULESETS)}
          >
            Rulesets
          </Button>,
          <Button
            variant="primary"
            onClick={() => {
              if (isPoppScoringLimitReached) {
                navigate(Paths.CONTACT_SUPPORT);
              } else {
                navigate(Paths.ANALYSE_CREATE);
              }
            }}
          >
            {isPoppScoringLimitReached
              ? 'Limit reached (Contact support)'
              : 'New analysis'}
          </Button>
        ]}
        containerSx={{
          mb: '40px'
        }}
      />

      {tab === TabsValue.ROLES && (
        <>
          <Box sx={headerContainer}>
            <Box sx={userActionsContainer}>
              <Box sx={{ mr: 2 }}>
                <SearchInput
                  value={searchRole}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchRole(e.target.value)
                  }
                  onClear={() => setSearchRole('')}
                  placeholder="Search jobs"
                />
              </Box>

              {isAdmin && (
                <Box sx={{ mt: 1 }}>
                  <Checkbox
                    label="Show all analysed roles"
                    onChange={() => {
                      setIsAllCampaigns((prev: boolean) => !prev);
                    }}
                    checked={isAllCampaigns}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <AnalysesTab
            isAllCampaigns={isAllCampaigns}
            searchText={searchRole}
            limit={limit}
            onChangeRowsPerPage={handleRowsPerPageChange}
          />
        </>
      )}
      {tab === TabsValue.APPLICANTS && (
        <ApplicantsTab onSubscriptionError={setApplicantsSubscriptionError} />
      )}
      {tab === TabsValue.SOURCED_CANDIDATES && (
        <SourceCandidatesTab
          onSubscriptionError={setSourcedCandidatesSubscriptionError}
          totalSourcedCandidates={sourcedCandidatesTotal}
        />
      )}
    </Box>
  );
};
